import React, { useState, useEffect, useCallback } from 'react';
import { Snackbar, Alert, AlertTitle } from '@mui/material';
import type { ApiErrorDetail } from '../../helpers/apiErrorEvent';

/**
 * Global error snackbar — mount once in App.tsx.
 *
 * Listens for 'api-error' window events dispatched by the Axios interceptor
 * and renders a top-right snackbar with:
 *   - A summary message (title / HTTP fallback)
 *   - A bulleted list of field-level validation errors when present
 *     (e.g. ASP.NET Core ProblemDetails { errors: { Field: ["msg"] } })
 */
export const ApiErrorSnackbar: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<ApiErrorDetail | null>(null);

  const handleApiError = useCallback((e: Event) => {
    const incoming = (e as CustomEvent<ApiErrorDetail>).detail;
    setDetail(incoming);
    setOpen(true);
  }, []);

  useEffect(() => {
    window.addEventListener('api-error', handleApiError);
    return () => window.removeEventListener('api-error', handleApiError);
  }, [handleApiError]);

  const handleClose = (_: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={6000}
      onClose={handleClose}
      sx={{ maxWidth: 480 }}
    >
      <Alert
        onClose={handleClose}
        severity="error"
        variant="filled"
      >
        {detail?.fieldErrors?.length ? (
          <>
            <AlertTitle sx={{ fontWeight: 600, mb: 0.5 }}>
              {detail.message}
            </AlertTitle>
            <ul style={{ margin: '2px 0 0', paddingLeft: '18px' }}>
              {detail.fieldErrors.map((msg, i) => (
                <li key={i} style={{ fontSize: '13px' }}>{msg}</li>
              ))}
            </ul>
          </>
        ) : (
          detail?.message
        )}
      </Alert>
    </Snackbar>
  );
};
